import { fetchCachedDashboardOverview } from "../../services/dashboardApi";
import { normalizeRole } from "../../rbac/access";
import { decodeJwtPayload, getStoredAccessToken } from "../../utils/authSession";
import { fetchPayrollHub } from "../payroll/payrollApi";

function isProcessedRun(run) {
  const exec = String(run?.execution_status || "").toLowerCase();
  const status = String(run?.status || "").toLowerCase();
  const lifecycle = String(run?.lifecycle_status || "").toLowerCase();
  return (
    exec === "completed" ||
    status === "processed" ||
    status === "locked" ||
    status === "approved" ||
    lifecycle === "locked"
  );
}

function isActiveRun(run) {
  return !isProcessedRun(run);
}

function flattenRuns(hub) {
  const periods = Array.isArray(hub?.periods) ? hub.periods : [];
  return periods.flatMap((p) =>
    (Array.isArray(p.runs) ? p.runs : []).map((r) => ({
      ...r,
      period_status: p.status,
      month_key: r.month_key || p.month_key,
    }))
  );
}

/** Live payroll home metrics — dashboard overview + payroll hub. */
export async function fetchPayrollHomeStats() {
  const [overviewRes, hubRes] = await Promise.all([
    fetchCachedDashboardOverview(normalizeRole(decodeJwtPayload(getStoredAccessToken())?.role)),
    fetchPayrollHub(),
  ]);

  const overview = overviewRes || {};
  const hub = hubRes?.data || {};
  const runs = flattenRuns(hub);
  const currentMonthKey = new Date().toISOString().slice(0, 7);

  const activePayrollRuns = runs.filter(isActiveRun).length;
  const thisMonthProcessed = runs.filter(
    (r) => r.month_key === currentMonthKey && isProcessedRun(r)
  ).length;
  const openPeriods = (hub.periods || []).filter(
    (p) => String(p.status || "open").toLowerCase() === "open"
  ).length;

  const compliancePending = Number(overview?.compliance?.open_items ?? 0);
  const pendingTasks = compliancePending + activePayrollRuns;

  return {
    asOf: overview.as_of || new Date().toISOString().slice(0, 10),
    totalEmployees: Number(overview?.employees?.total ?? 0),
    activePayrollRuns,
    openPayPeriods: openPeriods,
    thisMonthProcessed,
    pendingTasks,
    totalRuns: Number(hub.total_runs ?? runs.length),
    periodLabel: overview?.payroll?.period_label || null,
    processedCount: Number(overview?.payroll?.processed_count ?? 0),
    hasPayrollData: Boolean(overview?.payroll?.has_payroll_data),
  };
}
